import { SearchFilterType } from "@mirage/shared/search/search-filters";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import type { SearchOptions } from "@mirage/service-search/service";
import type { SearchFilter } from "@mirage/shared/search/search-filters";
import Sentry from "@mirage/shared/sentry";

export const SEARCH_QUERY_PARAM = "query";
export const SEARCH_FILTERS_PARAM = "filters";
export const BASE_SEARCH_RESULTS_URL = "/search_results";

export function useQueryParams() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export function isValidSearchFilter(
  parsedFilter: unknown,
): parsedFilter is SearchFilter {
  if (typeof parsedFilter !== "object" || parsedFilter === null) {
    return false;
  }
  if (
    !("type" in parsedFilter) ||
    !("id" in parsedFilter) ||
    !("parameters" in parsedFilter)
  ) {
    return false;
  }
  if (
    typeof parsedFilter.type !== "string" ||
    typeof parsedFilter.id !== "string" ||
    typeof parsedFilter.parameters !== "object"
  ) {
    return false;
  }
  return true;
}

// OSE-5463: Sentry scrubs the URL so we're not able to see what is causing the JSON parse error
// This will scrub the search params so we can see the shape of filtersParam to better understand the error
export function scrubFiltersParam(filtersParam: string) {
  return filtersParam.replace(/[a-zA-Z0-9]/g, "*");
}

export function getSearchFromURLParams(urlParams: URLSearchParams) {
  const query = urlParams.get(SEARCH_QUERY_PARAM) || "";
  const filtersParam = urlParams.get(SEARCH_FILTERS_PARAM);
  let parsedFilters;
  try {
    parsedFilters = filtersParam ? JSON.parse(filtersParam) : [];
  } catch (error) {
    Sentry.captureMessage(
      `[useQueryParams] Error parsing filtersParam: ${scrubFiltersParam(
        filtersParam ?? "",
      )}`,
    );
    parsedFilters = [];
  }
  const filters = Array.isArray(parsedFilters)
    ? parsedFilters.filter(isValidSearchFilter)
    : [];
  const searchOptions: Partial<SearchOptions> = {
    disableSpellCorrection: urlParams.get("disableSpellCorrection") === "true",
  };
  return {
    query,
    filters,
    searchOptions,
  };
}

// Only include required fields to avoid bloating URL
export function stripSearchFilterKeys(filter: SearchFilter) {
  switch (filter.type) {
    case SearchFilterType.Connector:
      return {
        id: filter.id,
        type: filter.type,
        parameters: {
          connectorId: filter.parameters.connectorId,
          displayName: filter.parameters.displayName,
        },
      };
    case SearchFilterType.Person:
    case SearchFilterType.LastUpdated:
    case SearchFilterType.ContentType:
      return filter;
    case SearchFilterType.Channel:
      return {
        id: filter.id,
        type: filter.type,
        parameters: {
          channelName: filter.parameters.channelName,
        },
      };
    default:
      filter satisfies never;
      return filter;
  }
}

export function generateSearchURL(
  query: string,
  filters?: SearchFilter[],
  options?: Partial<SearchOptions>,
  baseURL: string = BASE_SEARCH_RESULTS_URL,
) {
  const urlParams = new URLSearchParams();
  urlParams.set(SEARCH_QUERY_PARAM, query);

  if (
    filters?.filter((filter) => filter.type !== SearchFilterType.Channel).length
  ) {
    urlParams.set(
      SEARCH_FILTERS_PARAM,
      JSON.stringify(filters.map((filter) => stripSearchFilterKeys(filter))),
    );
  }

  if (options?.disableSpellCorrection) {
    urlParams.set("disableSpellCorrection", "true");
  }

  return `${baseURL}?${urlParams.toString()}`;
}
